/**
 * ModularStandsCatalog
 * ────────────────────
 * Inline catalog component: renders the modular stands grid + detail modal.
 * Used inside CustomerDashboard Stand Request tab.
 *
 * Props:
 *   tr (bool)             — language flag
 *   recommendedArea (num) — user's onboarding stand_area (m²). The matching
 *                           stand is pushed to the front with a "RECOMMENDED"
 *                           badge.
 *   onCartRefresh (func)  — called after a successful cart addition
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Package, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ModularStandsCatalog({ tr, recommendedArea }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [stands, setStands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('cop31_token');
        const r = await axios.get(`${API}/customer/modular-stands`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStands(r.data || []);
      } finally { setLoading(false); }
    };
    load();
  }, []);

  const sortedStands = React.useMemo(() => {
    if (!stands.length) return [];
    const recNum = Number(recommendedArea) || null;
    const copy = [...stands];
    copy.sort((a, b) => {
      const aMatch = recNum && Number(a.area_m2) === recNum ? 0 : 1;
      const bMatch = recNum && Number(b.area_m2) === recNum ? 0 : 1;
      if (aMatch !== bMatch) return aMatch - bMatch;
      return (a.area_m2 || 0) - (b.area_m2 || 0);
    });
    return copy;
  }, [stands, recommendedArea]);

  // Bilingual detail URL
  const openDetail = (standId) => {
    const segment = tr ? 'moduler-standlar' : 'modular-stands';
    navigate(`/musteri-admin/${slug}/${segment}/${standId}`);
  };

  if (loading) {
    return (
      <div className="py-10 text-center">
        <div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto" />
      </div>
    );
  }

  if (stands.length === 0) {
    return (
      <div className="text-center py-10 bg-gray-50 rounded-2xl border border-gray-200">
        <Package className="h-12 w-12 mx-auto mb-2 text-gray-300" />
        <p className="text-gray-500 text-sm">
          {tr ? 'Henüz modüler stand tanımlanmamış.' : 'No modular stands defined yet.'}
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="modular-stands-inline-grid">
      {sortedStands.map(st => {
        const isRecommended = recommendedArea && Number(st.area_m2) === Number(recommendedArea);
        return (
          <StandCard
            key={st.id}
            stand={st}
            tr={tr}
            recommended={!!isRecommended}
            onOpen={() => openDetail(st.id)}
          />
        );
      })}
    </div>
  );
}

/* ─── Card ──────────────────────────────────────────────────────────────── */
function StandCard({ stand, tr, recommended, onOpen }) {
  const title = tr ? (stand.title_tr || stand.title) : (stand.title_en || stand.title);
  const desc  = tr ? (stand.description_tr) : (stand.description_en);
  const pkg   = stand.package;
  return (
    <button
      onClick={onOpen}
      data-testid={`modular-stand-card-${stand.id}`}
      className={`text-left bg-white rounded-2xl border-2 hover:shadow-xl transition-all overflow-hidden group relative ${recommended ? 'border-indigo-500 ring-4 ring-indigo-100' : 'border-gray-200 hover:border-indigo-300'}`}
    >
      {recommended && (
        <div className="absolute top-3 right-3 z-10 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-[10px] font-black px-2.5 py-1 rounded-full shadow-lg flex items-center gap-1">
          <Sparkles className="h-3 w-3" />
          {tr ? 'SİZE TAVSİYE' : 'RECOMMENDED'}
        </div>
      )}
      <div className="aspect-[4/3] bg-gray-100 relative overflow-hidden">
        {stand.thumb_url || stand.cover_url ? (
          <img
            src={stand.thumb_url || stand.cover_url}
            alt={title}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="h-14 w-14 text-gray-300" />
          </div>
        )}
        {stand.area_m2 && (
          <span className="absolute top-3 left-3 bg-indigo-600 text-white text-[11px] font-black px-2.5 py-1 rounded-full tracking-widest shadow">
            {stand.area_m2} m²
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-bold text-gray-900 leading-tight mb-1 line-clamp-2">{title}</h3>
        {desc && <p className="text-xs text-gray-500 line-clamp-2">{desc}</p>}

        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
          <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider">
            {pkg ? (tr ? `Paket (${pkg.items?.length || 0})` : `Package (${pkg.items?.length || 0})`) : (tr ? 'Paket hazır değil' : 'Package not ready')}
          </span>
          {stand.price > 0 && (
            <span className="font-bold text-indigo-900 text-sm">
              {Number(stand.price || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })} {stand.currency}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

